import { Button } from "@/components/ui/baseUi/button"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import { PencilEditIcon } from "@images/svgIcons/pencilEditIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { LeadType } from "@modules/boxLead/entities/boxLeadTypes"
import { useBoxLeadDelete } from "@modules/boxLead/hooks/useBoxLeadActions"
import { useBoxLead } from "@modules/boxLead/hooks/useBoxLeadDetails"
import { useBoxLeadSidebar } from "@modules/boxLead/hooks/useBoxLeadSidebar"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import {
  MemberSendOfferModal,
  type MemberSendOfferModalRef,
} from "@modules/members/sendOfferToMember/components/memberSendOfferModal"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { formatPhoneNumber } from "@modules/utils/numberUtils"
import dayjs from "dayjs"
import { type CountryCode } from "libphonenumber-js"
import { CircleFadingPlus, Hourglass, Mail, Phone, Trash2 } from "lucide-react"
import { useRef } from "react"

type BoxLeadOverviewProps = {
  leadId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxLeadOverview({ leadId, className, ...props }: BoxLeadOverviewProps) {
  const confirmPopup = useConfirmPopup()
  const { hideSidebar } = useSidebar()
  const { data: leadDetails } = useBoxLead(leadId)
  const { deleteBoxLead, isPending: isPendingDelete } = useBoxLeadDelete()
  const { navigateToBoxLeadForm } = useBoxLeadSidebar()
  const canEditLead = useBoxStaffPermission(BoxStaffPermissions.MANAGE_LEAD)

  const memberSendOfferModalRef = useRef<MemberSendOfferModalRef>(null)
  const bodyDeletePopup = (
    <div className="flex flex-col content-center items-center justify-center gap-4 pb-4">
      <div className="flex flex-col gap-1">
        <Text fontStyle="TL" className="flex justify-center text-center text-textDefault-heading">
          {word("box.lead.overview.modal.delete.header")}
        </Text>
        <Text fontStyle="BS" className="flex justify-center text-center text-textDefault-caption">
          {word("box.lead.overview.modal.delete.warning")}
        </Text>
      </div>
      <Text fontStyle="BM" className="flex justify-center text-center text-textDefault-body">
        {word("box.lead.overview.modal.delete.content")}
      </Text>
    </div>
  )

  const handleSendOfferModal = () => {
    memberSendOfferModalRef.current?.open()
  }

  const onRemove = () => {
    confirmPopup.show({
      classNames: {
        dialogContent: "w-104",
        dialogFooter: "flex flex-row justify-around w-full",
        dialogFooterConfirm: "flex-1",
        dialogFooterCancel: "flex-1",
      },
      title: "",
      message: bodyDeletePopup,
      accept: async () => {
        await deleteBoxLead(leadId)
          .then(() => {
            toast.success(word("box.lead.form.toast.delete.success"), {
              classNames: successToastClassName(),
            })
            hideSidebar(false)
          })
          .catch(() => {
            toast.error(word("box.lead.form.toast.delete.error"), {
              classNames: errorToastClassName(),
            })
          })
      },
      footerProps: {
        cancelText: word("global.cancel"),
        confirmText: word("global.delete"),
      },
    })
  }

  const onEdit = () => {
    navigateToBoxLeadForm(leadDetails)
  }

  return (
    <div className={cn("flex w-full flex-col gap-8 px-5 pt-5 pb-8", className)} {...props}>
      {/* HEADER */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3">
          <div className="flex flex-col gap-1">
            <Text fontStyle="LS" className="text-textDefault-caption">
              {word("box.lead.overview.content.firstContact.label").toUpperCase()}
            </Text>
            <Text fontStyle="TS" className="text-textDefault-body">
              {formatDateWithHyphen(dayjs(leadDetails.firstContact).format("L"))}
            </Text>
          </div>
        </div>
        <div className="col-span-4">
          <div className="flex flex-row items-center gap-1">
            <Hourglass
              size={24}
              color="currentColor"
              strokeWidth={1.5}
              className={leadDetails.getColorFirstContact()}
            />
            <Text fontStyle="BM" className="text-textDefault-body">
              {word("box.lead.overview.content.firstContact.hourglassText", {
                count: dayjs().diff(dayjs(leadDetails.firstContact), "day"),
              })}
            </Text>
          </div>
        </div>
        <div className="col-span-5">
          <div className="flex flex-col gap-1">
            <Text fontStyle="LS" className="text-textDefault-caption">
              {word("box.lead.overview.content.madeBy.label").toUpperCase()}
            </Text>
            {leadDetails.madeByStaff ? (
              <UserCell
                member={{
                  avatar: leadDetails.madeByStaff.user.avatar,
                  firstname: leadDetails.madeByStaff.user.firstname,
                  lastname: leadDetails.madeByStaff.user.lastname,
                }}
                subtitle={displayRole(leadDetails.madeByStaff.staffMemberRole)}
                subtitleClassName="text-neutral-600"
              />
            ) : (
              <div className="flex w-full flex-row items-center justify-center gap-2">
                <CircleFadingPlus size={24} color="currentColor" className="w-2/12 text-textDefault-body" />
                <div className="flex w-10/12 flex-col justify-center gap-1">
                  <Text fontStyle="LL" style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}>
                    {leadDetails.leadType === LeadType.MANUAL_ADDITION
                      ? leadDetails.getWordingType()
                      : word("box.lead.overview.content.madeBy.automaticAddition")}
                  </Text>
                  <Text
                    fontStyle="LS"
                    className="text-textDefault-caption"
                    style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
                  >
                    {leadDetails.leadType === LeadType.MANUAL_ADDITION
                      ? word("box.lead.overview.content.madeBy.manualAddition")
                      : leadDetails.getWordingType()}
                  </Text>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CONTACT */}
      <div className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("global.form.label.contact")}
        </Text>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-7 flex flex-row items-center gap-2">
            <Mail strokeWidth={1.5} color="currentColor" size={22} className="text-textDefault-body" />
            <Text
              fontStyle="TM"
              className="w-full overflow-hidden text-textDefault-body"
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            >
              {leadDetails.email ?? word("box.lead.overview.content.contact.noEmail")}
            </Text>
          </div>
          <div className="col-span-5 flex flex-row items-center gap-2">
            <Phone strokeWidth={1.5} color="currentColor" size={22} className="text-textDefault-body" />
            <Text fontStyle="TM" className="text-textDefault-body">
              {leadDetails.phoneNumber?.number
                ? formatPhoneNumber(leadDetails.phoneNumber.number, leadDetails.phoneNumber.countryCode as CountryCode)
                : word("box.lead.overview.content.contact.noPhoneNumber")}
            </Text>
          </div>
        </div>
      </div>

      {/* GOALS AND EXPERIENCE LEVEL */}
      <div className="flex flex-row justify-between">
        <div className="flex max-w-96 flex-col gap-2">
          <Text fontStyle="LS" className="text-textDefault-caption">
            {word("box.lead.overview.content.goals.label", { count: leadDetails.goals?.length ?? 1 }).toUpperCase()}
          </Text>
          <Text
            fontStyle="BM"
            style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            className={cn((leadDetails.goals?.length ?? 0 > 0) ? "text-textDefault-body" : "text-textDefault-caption")}
          >
            {leadDetails.getWordingGoals()}
          </Text>
        </div>
        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="text-textDefault-caption">
            {word("box.lead.overview.content.experienceLevel.label").toUpperCase()}
          </Text>
          <Text
            fontStyle="BM"
            className={cn(leadDetails.experienceLevel ? "text-textDefault-body" : "text-textDefault-caption")}
          >
            {leadDetails.getWordingExperienceLevel()}
          </Text>
        </div>
      </div>

      {/* HEARD ABOUT US */}
      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" className="text-textDefault-caption">
          {word("box.lead.overview.content.hearAboutUs.label").toUpperCase()}
        </Text>
        <Text
          fontStyle="BM"
          className={cn(leadDetails.hearAboutUs ? "text-textDefault-body" : "text-textDefault-caption")}
        >
          {leadDetails.getWordingHeardAboutUs()}
        </Text>
      </div>

      {/* BEST TIME TO CALL */}
      <div className="flex flex-col gap-2">
        <Text fontStyle="LS" className="text-textDefault-caption">
          {word("box.lead.overview.content.bestTimeToContact.label").toUpperCase()}
        </Text>
        <Text
          fontStyle="BM"
          className={cn(leadDetails.bestTimeToContact ? "text-textDefault-body" : "text-textDefault-caption")}
          style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
        >
          {leadDetails.getWordingBestTimeToContact()}
        </Text>
      </div>

      {/* INTERNAL NOTE*/}
      <div className="flex flex-col gap-2">
        <Text fontStyle="TM" className="text-textDefault-body">
          {word("global.form.label.internalNote")}
        </Text>
        <div className="mt-2 mb-2 flex flex-row gap-2">
          <WarningIcon color="currentColor" width={24} height={24} className="w-1/12 text-textDefault-body" />
          <Text fontStyle="BS" className="text-textDefault-caption">
            {word("box.lead.form.input.internalNote.warning")}
          </Text>
        </div>
        <Text fontStyle="BM" className="text-textDefault-body wrap-anywhere">
          {leadDetails.note ?? word("box.lead.overview.content.internalNote.emptyNote")}
        </Text>
      </div>

      {/* ADD SEND OFFER */}
      {leadDetails.user?.id ? (
        <div
          onClick={canEditLead ? handleSendOfferModal : undefined}
          className={cn(
            "mx-1 flex items-center justify-center gap-2 rounded-2xl border-2 border-borderPrimary bg-neutral-50 p-2",
            canEditLead ? "shiny-hover cursor-pointer" : "cursor-not-allowed opacity-50",
          )}
        >
          <img src={boxOfferPlanIcon} />
          <Text fontStyle="TS" className="text-textDefault-body">
            {word("box.member.table.offer.menu.sendOffer")}
          </Text>
        </div>
      ) : null}

      {/* BUTTONS */}
      {canEditLead && (
        <div className="mb-6 flex w-full flex-row items-center justify-center gap-2">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={onRemove}
            disabled={isPendingDelete || !canEditLead}
          >
            <Trash2 color="currentColor" size={15} />
            {word("box.lead.overview.content.button.deleteLead")}
          </Button>

          <Button variant="primary" size="lg" className="flex-1" onClick={onEdit} disabled={!canEditLead}>
            <PencilEditIcon color="currentColor" size={15} />
            {word("global.edit")}
          </Button>
        </div>
      )}

      {leadDetails.user?.id ? (
        <MemberSendOfferModal
          userId={leadDetails.user.id}
          memberId={leadDetails.boxMemberLight?.boxMemberId}
          isSendingToLead={true}
          ref={memberSendOfferModalRef}
        />
      ) : null}
    </div>
  )
}
